/**
 * Image Proxy Helper
 * Utilities for working with image proxy service
 */

/**
 * Get the image proxy URL (NestJS microservice or Next.js API)
 */
export function getImageProxyUrl(): string {
  return process.env.NEXT_PUBLIC_IMAGE_PROXY_URL || '';
}

/**
 * Check if NestJS microservice is available
 */
export function isNestJSProxyAvailable(): boolean {
  return !!process.env.NEXT_PUBLIC_IMAGE_PROXY_URL;
}

/**
 * Build proxy URL for an image
 * @param encodedUrl - Base64 encoded image URL
 * @param useNestJS - Force use of NestJS service (default: auto-detect)
 */
export function buildProxyUrl(encodedUrl: string, useNestJS?: boolean): string {
  const shouldUseNestJS = useNestJS ?? isNestJSProxyAvailable();
  const cacheBuster = `_t=${Date.now()}`;
  
  if (shouldUseNestJS) {
    const baseUrl = getImageProxyUrl();
    return `${baseUrl}/api/image-proxy?url=${encodedUrl}&${cacheBuster}`;
  }
  
  // Fallback to Next.js API route
  return `/api/image-proxy?url=${encodedUrl}&${cacheBuster}`;
}

/**
 * Fetch proxied image
 * @param encodedUrl - Base64 encoded image URL
 * @param useNestJS - Force use of NestJS service (default: auto-detect)
 */
export async function fetchProxiedImage(
  encodedUrl: string,
  useNestJS?: boolean,
): Promise<string> {
  const url = buildProxyUrl(encodedUrl, useNestJS);
  
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.dataUrl;
  } catch (error) {
    console.error('Failed to fetch proxied image:', error);
    throw error;
  }
}

/**
 * Check health of NestJS image proxy service
 */
export async function checkImageProxyHealth(): Promise<{
  available: boolean;
  status?: string;
  redis?: string;
}> {
  if (!isNestJSProxyAvailable()) {
    return { available: false };
  }
  
  try {
    const baseUrl = getImageProxyUrl();
    const response = await fetch(`${baseUrl}/health`, {
      method: 'GET',
      cache: 'no-store',
    });
    
    if (!response.ok) {
      return { available: false };
    }
    
    const data = await response.json();
    return {
      available: true,
      status: data.status,
      redis: data.redis,
    };
  } catch (error) {
    console.error('Health check failed:', error);
    return { available: false };
  }
}

